import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { EvaluationBaseProps, EvaluationValue, EvaluationSelector } from '../EvaluationSelector';
import { EvaluationEntry } from './evaluationTypes';

export * from './evaluationTypes';

interface MultipleEvaluationsProps extends EvaluationBaseProps {
  name: string;
  evaluations: EvaluationEntry[];
  values?: Record<string, EvaluationValue | undefined>;
  onChange?: (values: Record<string, EvaluationValue>) => void;
  defaultValue?: EvaluationValue;
}

// MultipleEvaluations is a component that displays a list of EvaluationSelector components (e.g. for evaluating pain
// levels).  This currently used in the AdvancedImageDiagnosis component to capture evaluations for each location on an
// image and in the CheckboxGroup component to display the evaluations expanded checkboxes.
export const MultipleEvaluations: FunctionComponent<MultipleEvaluationsProps> = ({
  name,
  evaluations,
  values = {},
  onChange = () => {},
  defaultValue,
  ...selectorProps
}) => {
  const [evaluationValues, setEvaluationValues] = useState<Record<string, EvaluationValue>>({});

  const getValueForEvaluation = useCallback(
    (evaluationName: string): EvaluationValue | undefined => values?.[evaluationName] || defaultValue,
    [name]
  );

  useEffect(() => {
    onChange(evaluationValues);
  }, [evaluationValues]);

  return (
    <div className="flex flex-col gap-y-4">
      {evaluations.map(({ label, value: evaluationName }) => (
        <EvaluationSelector
          {...selectorProps}
          key={`dx-for-location-${name}-evaluation-${evaluationName}`}
          name={evaluationName}
          label={label}
          value={getValueForEvaluation(evaluationName)}
          onChange={(ev) => {
            setEvaluationValues((current): Record<string, EvaluationValue> => {
              if (ev == undefined) {
                // Use the defaultValue if it's defined
                if (defaultValue !== undefined) {
                  return {
                    ...current,
                    [evaluationName]: defaultValue
                  };
                }

                // If ev is not defined (and there isn't a defaultValue), then remove the entry from the
                // evaluationValues
                delete current[evaluationName];
                return current;
              }

              return {
                ...current,
                [evaluationName]: ev
              };
            });
          }}
        />
      ))}
    </div>
  );
};
